import { computed, ref, nextTick } from 'vue';
import { merge } from 'lodash-es';
import type { ContextMenuItems } from '@/common/utils';
import type { Emits, Props } from '../../modernGrid.setup';
import type {
  GridRowDataType,
  GridRawRowDataType,
  GridRowMenuOption,
  GridRowInstance,
} from '../../modernGrid.types';
import { defaultRowMenuOption } from '../../modernGrid.define';

export const useRowMenu = <RowDataType extends GridRawRowDataType>({
  props,
  emit,
}: {
  props: Props<RowDataType>;
  emit: Emits<RowDataType>;
}) => {
  const rowMenuInfo = computed<GridRowMenuOption>(() =>
    merge({}, defaultRowMenuOption, props.option?.rowMenu),
  );
  const rowMenuRef = ref();
  const rowMenuItems = ref<ContextMenuItems[]>([]);

  /**
   * 메뉴 아이템의 click 핸들러를 래핑하여 contextmenuInfo에 행 데이터를 주입
   */
  const wrapMenuItemsWithRowData = (
    items: ContextMenuItems[],
    rowData: GridRowDataType<RowDataType>,
  ): ContextMenuItems[] => {
    return items.map((item) => {
      const wrappedItem: ContextMenuItems = { ...item };

      // click 핸들러가 있으면 래핑하여 contextmenuInfo 주입
      if (item.click) {
        const originalClick = item.click;
        wrappedItem.click = (param) => {
          originalClick({
            ...param,
            contextmenuInfo: [rowData],
          });
        };
      }

      // children이 있으면 재귀적으로 처리
      if (item.children?.length) {
        wrappedItem.children = wrapMenuItemsWithRowData(item.children, rowData);
      }

      return wrappedItem;
    });
  };

  const getColumnMenuItems = (row: GridRowInstance<RowDataType>): ContextMenuItems[] => {
    const { use: useMenu, menuItems } = rowMenuInfo.value || {};
    if (!useMenu) {
      return [];
    }
    // 각 메뉴 아이템에 row.original 데이터를 contextmenuInfo로 주입
    return wrapMenuItemsWithRowData(
      [...menuItems].filter(Boolean) as ContextMenuItems[],
      row.original,
    );
  };

  const onClickRowMenuBtn = async ({
    row,
    e,
  }: {
    row: GridRowInstance<RowDataType>;
    e: MouseEvent;
  }): Promise<void> => {
    emit('click-row', {
      event: e,
      rowData: row.original,
      rowIndex: row.index,
      cellIndex: '-1',
      cellName: 'row-menu',
    });

    // 부모 컴포넌트에서 메뉴 아이템(children 등)을 수정할 수 있으므로
    // nextTick으로 반응성 업데이트 완료 후 메뉴 표시
    await nextTick();
    rowMenuItems.value = getColumnMenuItems(row);
    rowMenuRef.value?.show(e);
  };

  const onContextMenuRow = async ({
    row,
    e,
  }: {
    row: GridRowInstance<RowDataType>;
    e: MouseEvent;
  }): Promise<void> => {
    if (!rowMenuInfo.value.use) return;

    e.preventDefault();

    emit('click-row', {
      event: e,
      rowData: row.original,
      rowIndex: row.index,
      cellIndex: '-1',
      cellName: 'context-menu',
    });

    // 부모 컴포넌트에서 메뉴 아이템(children 등)을 수정할 수 있으므로
    // nextTick으로 반응성 업데이트 완료 후 메뉴 표시
    await nextTick();
    rowMenuItems.value = getColumnMenuItems(row);
    rowMenuRef.value?.show(e);
  };

  return {
    rowMenuInfo,
    rowMenuRef,
    rowMenuItems,
    onClickRowMenuBtn,
    onContextMenuRow,
  };
};
